import { SiteAddress } from 'api/addresssearch/schema';
import clsx from 'clsx';
import FormSection from 'components/FormSection';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import { useEffect, useState } from 'react';
import { Add, BusinessOutline } from 'react-ionicons';
import {
  components,
  DropdownIndicatorProps,
  MenuListProps,
  SingleValueProps,
} from 'react-select';
import GlowingBorder from 'screens/AccessExpressV3/BaseComponents/GlowingBorder';

const AddressSingleValue = (props: SingleValueProps<OptionType, false>) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <BusinessOutline width="16px" height="16px" color="#F8FAFC" />
        <span>{data.label}</span>
      </div>
    </components.SingleValue>
  );
};

const AddressDropdownIndicator = (
  props: DropdownIndicatorProps<OptionType, false> & {
    matchType: 'none' | 'single' | 'multiple';
    macnum?: string;
    showDropdownCaret?: boolean;
  },
) => {
  const { matchType, macnum, showDropdownCaret } = props;

  if (matchType === 'none') {
    return (
      <p
        className={clsx(
          'flex w-full items-start justify-start gap-2 rounded-md py-2 text-sm font-bold text-content-base-subdued',
        )}
      >
        No account found{' '}
      </p>
    );
  }

  return (
    <components.DropdownIndicator {...props}>
      <div className="flex items-center justify-center gap-2">
        {macnum && (
          <p
            className={clsx(
              'flex w-full items-start justify-start gap-2 rounded-md py-2 text-sm font-bold text-content-base-subdued',
            )}
          >
            Account #{macnum}
          </p>
        )}
        {showDropdownCaret && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: props.selectProps.menuIsOpen
                ? 'rotate(180deg)'
                : 'rotate(0deg)',
            }}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M2.96967 5.21967C3.26256 4.92678 3.73744 4.92678 4.03033 5.21967L8 9.18934L11.9697 5.21967C12.2626 4.92678 12.7374 4.92678 13.0303 5.21967C13.3232 5.51256 13.3232 5.98744 13.0303 6.28033L8.53033 10.7803C8.23744 11.0732 7.76256 11.0732 7.46967 10.7803L2.96967 6.28033C2.67678 5.98744 2.67678 5.51256 2.96967 5.21967Z"
              fill="#94A3B8"
            />
          </svg>
        )}
      </div>
    </components.DropdownIndicator>
  );
};

const AddressMenuList = (
  props: MenuListProps<OptionType, false> & {
    onCreateNewAccount: () => void;
  },
) => {
  const { children, onCreateNewAccount, selectProps } = props;

  const handleCreateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    selectProps?.onMenuClose?.();
    onCreateNewAccount();
  };

  return (
    <div className="relative">
      <div
        className={clsx('pointer-events-none absolute inset-0')}
        style={{
          background:
            'linear-gradient(134deg, #7E77B3, #8989FF, #6C9DFF, #78CFDA, #9FF4A9)',
          WebkitMask:
            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          padding: `1px`,
          paddingTop: 0,
          borderRadius: '4px',
          borderTopRightRadius: 0,
          borderTopLeftRadius: 0,
        }}
      />
      <components.MenuList {...props}>
        {children}
        <div className="m-[10px] mt-0">
          <button
            type="button"
            className={clsx(
              'flex w-auto items-start justify-start gap-2 rounded-md p-2.5 px-[5px] pb-0  text-sm font-medium text-content-accent-default',
              '!text-accent-blue',
            )}
            onClick={handleCreateClick}
          >
            <p>Create new account</p>
            <Add color="inherit" width="18px" height="18px" />
          </button>
        </div>
      </components.MenuList>
    </div>
  );
};

interface AddressInputProps {
  locations: (SiteAddress & { account_name: string; site_name: string })[];
  className?: string;
  onLocationSelected?: (location: SiteAddress | null) => void;
  location: {
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    zip_code: string;
    id?: string;
  } | null;
  onCreateNewAccount?: () => void;
  showDropdownCaret?: boolean;
  value?: SiteAddress | null;
  selectedOption: OptionType | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<OptionType | null>>;
}

const AddressInput = ({
  locations,
  className,
  onLocationSelected,
  location,
  onCreateNewAccount,
  showDropdownCaret = true,
  selectedOption,
  setSelectedOption,
}: AddressInputProps) => {
  const [initialized, setInitialized] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const matchType =
    locations.length === 0
      ? 'none'
      : locations.length === 1
        ? 'single'
        : 'multiple';

  useEffect(() => {
    if (!selectedOption) {
      setInitialized(false);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (initialized || selectedOption) return;

    if (locations.length > 0) {
      const first = {
        label: [
          locations[0].address_line_1,
          locations[0].city,
          locations[0].state,
          locations[0].zip,
        ]
          .filter(Boolean)
          .join(', '),
        value: locations[0].account_name,
      };
      setSelectedOption(first);
      onLocationSelected?.(locations[0]);
      setInitialized(true);
    } else if (location) {
      const fallback = {
        label: [
          location.address1,
          location.city,
          location.state,
          location.zip_code,
        ]
          .filter(Boolean)
          .join(', '),
        value: location.address1,
      };
      setSelectedOption(fallback);
      //@ts-expect-error err
      onLocationSelected?.(location);
      setInitialized(true);
    }
  }, [
    locations,
    location,
    onLocationSelected,
    initialized,
    setSelectedOption,
    selectedOption,
  ]);

  const options =
    matchType === 'none'
      ? location
        ? [
            {
              label: [
                location.address1,
                location.city,
                location.state,
                location.zip_code,
              ]
                .filter(Boolean)
                .join(', '),
              value: location.address1,
            },
          ]
        : []
      : locations.map((loc) => ({
          label: [loc.address_line_1, loc.city, loc.state, loc.zip]
            .filter(Boolean)
            .join(', '),
          value: loc.macnum as string,
          subvalue: [loc?.parent_macnum, loc.parent_name, loc.name]
            .filter(Boolean)
            .join(', '),
        }));

  return (
    <FormSection
      title="Service address"
      gridClassName="relative grid-cols-1"
      className={className}
    >
      <GlowingBorder
        borderRadius={4}
        borderWidth={1}
        borderClassName={clsx(isMenuOpen && '!rounded-b-none')}
      />

      <GraniteSelect<OptionType, false>
        variant="ia"
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        options={options}
        menuIsOpen={showDropdownCaret ? undefined : false}
        components={{
          SingleValue: AddressSingleValue,
          DropdownIndicator: (props) => (
            <AddressDropdownIndicator
              {...props}
              matchType={matchType}
              macnum={selectedOption?.value}
              showDropdownCaret={showDropdownCaret}
            />
          ),
          MenuList: (props) => (
            <AddressMenuList
              {...props}
              onCreateNewAccount={() => {
                onCreateNewAccount?.();
              }}
            />
          ),
        }}
        value={selectedOption ?? undefined}
        onChange={(selected) => {
          setSelectedOption(selected);
          const selectedLocation = locations.find(
            (loc) => loc.macnum === selected?.value,
          );
          onLocationSelected?.(selectedLocation || null);
        }}
        noOptionsMessage={() => null}
      />
    </FormSection>
  );
};

export default AddressInput;
